import { useReadOnly } from 'utils/lifecycle'
import React, { useCallback, useState } from 'react'
import {
  Button,
  Alert,
  Card,
  CardActions,
  CardContent,
  Grid,
  Typography,
  Link,
} from '@mui/material'
import {
  Archive,
  Edit,
  Pause,
  PlayArrow as Play,
  Layers as Clone,
  Payment,
  AttachMoney,
} from '@mui/icons-material'
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, IconButton, Tooltip, Dialog, DialogTitle, DialogContent } from '@mui/material';
import { useSnackbar } from 'notistack'
import moment from 'moment'
import * as offerActions from './utils'
import { canEditOffer, canArchiveOffer, canPauseOffer, canActivateOffer, normalizeOfferStatus } from 'utils/offerPermissions'
import { ConfirmationDialog } from 'components'
import { dateTimeFormat } from 'utils/time'
import { offerStatusEnum } from 'utils/constants'
import { businessPath } from 'utils/businessRoutes'
import { SUPPORT_EMAILS, mailto } from 'utils/supportEmails'
import { useNavigate, useParams } from 'react-router-dom'
import OfferPreviewCard from './components/OfferPreviewCard'
import { getApiErrorMessage } from 'utils/apiErrors'

const OfferCard = ({
  clicks,
  offer: details,
  editOffer,
  impressions,
  offerType,
  offerTypes,
  onOfferDataRefresh,
  onCloneDraft,
  spend,
}) => {
  const lifecycleReadOnly = useReadOnly()
  const readOnly = lifecycleReadOnly || details.moderation?.viewOnly
  const correctionRequired = details.moderation?.correctionRequired === true
  const correctionReason = details.moderation?.activationBlockedReason || 'Update and save customer-visible offer content before reactivating.'
  const underReview = details.moderation?.state === 'UNDER_REVIEW'
  const moderationState = details.moderation?.state
  const adminDisabled = moderationState === 'DISABLED'
  const moderationLockedArchived = normalizeOfferStatus(details.status) === 'ARCHIVED' && (underReview || adminDisabled)
  const offenseHistory = details.moderation?.offenseHistory || []
  const currentFirstOffensePause = moderationState === 'FIRST_OFFENSE' && details.status === offerStatusEnum.paused && (correctionRequired || !details.statusReason || details.statusReason.startsWith('offer-offense:'))
  const historicalModeration = moderationState === 'FIRST_OFFENSE' && !currentFirstOffensePause && details.moderation?.offenseCount > 0
  const showModeration = moderationState && moderationState !== 'CLEAR' && !historicalModeration
  const moderationReason = adminDisabled ? 'This offer is view-only.' : underReview ? 'This offer is view-only while under review.' : details.moderation?.reason
  const supportContact = (underReview || adminDisabled) && <> For details, contact <Link href={mailto(SUPPORT_EMAILS.help)} color="primary.main" sx={{ overflowWrap: 'anywhere', textUnderlineOffset: '0.15em' }}>{SUPPORT_EMAILS.help}</Link>.</>
  const feedbackExplanation = adminDisabled ? '' : details.moderation?.feedbackExplanation
  const moderationSeverity = moderationState === 'FIRST_OFFENSE' ? 'warning' : 'error'
  const moderationTitle = moderationState === 'FIRST_OFFENSE' ? 'First offense' : underReview && details.moderation?.offenseCount === 2 ? 'Second offense — under review' : underReview ? 'Offer under review' : null
  const statusReason = adminDisabled ? 'Disabled by an administrator' : details.statusReason?.startsWith('offer-offense:')
    ? (details.status === offerStatusEnum.paused ? 'Paused after first offense' : 'Archived for review')
    : details.statusReason?.startsWith('offer-disable:')
      ? 'Disabled by an administrator'
      : details.statusReason
  const statusColor = adminDisabled || details.statusReason?.startsWith('offer-offense:') ? 'error.main' : 'text.secondary'
  const [showConfirmation, setShowConfirmation] = useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate();
  const { businessId, organizationId } = useParams()

  const [showPreview, setShowPreview] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const [leaveTimeout, setLeaveTimeout] = useState(null);
  const [autoCloseTimeout, setAutoCloseTimeout] = useState(null);


const toggleLock = () => {
  clearTimeout(hoverTimeout);
  clearTimeout(leaveTimeout);
  clearTimeout(autoCloseTimeout); // Stop auto-close when pinned

  setIsLocked(prev => !prev);
  setShowPreview(true);
};

  const handlePauseOffer = useCallback(() => {
    offerActions
      .pauseOffer(details.offerId)
      .then(() => {
      enqueueSnackbar('Offer paused.', { variant: 'success' });
      onOfferDataRefresh?.([offerType, offerTypes.paused]);
    })
      .catch(error => {
        enqueueSnackbar(getApiErrorMessage(error, 'Couldn’t pause offer. Please try again.'), { variant: 'error' })
      })
  }, [details.offerId, enqueueSnackbar, offerType, offerTypes.paused, onOfferDataRefresh, readOnly, correctionRequired])

  const handleActivateOffer = useCallback(() => {
    if (readOnly || correctionRequired) return;
    offerActions
      .activateOffer(details.offerId)
      .then(response => {
      if (response.status === 'EXPIRED') {
        enqueueSnackbar('Offer expired.', { variant: 'success' });
      } else if (response.status === 'SCHEDULED') {
        enqueueSnackbar('Offer scheduled.', { variant: 'success' });
      } else {
        enqueueSnackbar('Offer activated.', { variant: 'success' });
      }

      onOfferDataRefresh?.([offerTypes.paused, response.status]);
    })
      .catch(error => {
        enqueueSnackbar(
          getApiErrorMessage(error, 'Couldn’t activate offer. Please try again.'),
          { autoHideDuration: 10_000, variant: 'error' }
        )
        onOfferDataRefresh?.([offerType])
      })
  }, [details.offerId, enqueueSnackbar, offerType, offerTypes.paused, onOfferDataRefresh, readOnly, correctionRequired])

  const handleCloneOffer = useCallback(() => {
    offerActions
      .cloneOffer(details.offerId)
      .then(draft => {
      enqueueSnackbar('Offer copied to a new draft.', { variant: 'success' });
      onOfferDataRefresh?.([offerTypes.draft]);
      onCloneDraft?.(draft);
    })
      .catch(error => {
        enqueueSnackbar(getApiErrorMessage(error, 'Couldn’t copy offer. Please try again.'), { variant: 'error' })
      })
  }, [details.offerId, enqueueSnackbar, offerTypes.draft, onOfferDataRefresh, onCloneDraft])

  const handleArchiveOffer = useCallback(() => {
    offerActions
      .deleteOffer(details.offerId)
      .then(() => {
  enqueueSnackbar('Offer archived.', { variant: 'success' });
  onOfferDataRefresh?.([offerType, offerTypes.archived]);
  setShowConfirmation(false);
})
      .catch(error => {
        enqueueSnackbar(getApiErrorMessage(error, 'Couldn’t archive offer. Please try again.'), { variant: 'error' })
      })
  }, [details.offerId, enqueueSnackbar, offerType, offerTypes.archived, onOfferDataRefresh])

  const handleAddPaymentOffer = useCallback(() => {
    navigate(businessPath(businessId, organizationId, 'payments'))
  }, [navigate])

  return (<>
    <Card>
      <CardContent style={{ padding: '8px 16px 0' }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={7} sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ overflowWrap: 'anywhere' }}>{details.title}</Typography>
            <Typography variant="body2" gutterBottom>
              {details.description}
            </Typography>
            {statusReason &&
              ((statusReason.endsWith(' on ') && (
                <Typography variant="caption" color={statusColor} sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                  {statusReason}{' '}
                  {moment(moment.utc(
                      statusReason.startsWith('Scheduled') ?
                          details.startDate : details.endDate
                      ).toDate()).format(
                    dateTimeFormat
                  )}
                </Typography>
              )) ||
                (statusReason.startsWith('Live') && (
                  <Typography variant="caption" color={statusColor} sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                    {statusReason}, ends on{' '}
                    {moment(moment.utc(details.endDate).toDate()).format(
                      dateTimeFormat
                    )}
                  </Typography>
                )) || (
                  <Typography variant="caption" color={statusColor} sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                    {statusReason}
                  </Typography>
                ))}
          </Grid>
          <Grid
            container
            item
            xs={12}
            sm={5}
            alignItems="center"
            justifyContent="space-evenly"
          >
            <div style={{ textAlign: 'center' }}>
              <Typography variant="h6">{clicks}</Typography>
              <Typography variant="body2" color="textSecondary">
                Claims
              </Typography>
            </div>
            <div style={{ textAlign: 'center' }}>
              <Typography variant="h6">{impressions}</Typography>
              <Typography color="textSecondary" variant="body2">
                Impressions
              </Typography>
            </div>
            <div style={{ textAlign: 'center' }}>
              <Typography variant="h6">{spend}</Typography>
              <Typography color="textSecondary" variant="body2">
                Spend
              </Typography>
            </div>
          </Grid>
        </Grid>
      </CardContent>
      {showModeration && <Alert
        severity={moderationSeverity}
        icon={false}
        sx={{
          mx: 2,
          mt: 1.5,
          mb: 1,
          px: 2,
          py: 1.25,
          bgcolor: 'background.section',
          color: 'text.primary',
          border: 0,
          borderLeft: '3px solid',
          borderColor: `${moderationSeverity}.main`,
          borderRadius: 1,
          '& .MuiAlert-message': { p: 0, width: '100%', maxWidth: '80ch', minWidth: 0, whiteSpace: 'normal', overflowWrap: 'anywhere' },
        }}
      >
        {moderationTitle && <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{moderationTitle}</Typography>}
        <Typography variant="body2" color="text.secondary" sx={{ mt: moderationTitle ? 0.5 : 0, lineHeight: 1.6 }}>{feedbackExplanation || moderationReason || 'Offense history is retained.'}{!feedbackExplanation && supportContact}</Typography>
        {feedbackExplanation && moderationReason && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.6 }}>{moderationReason}{supportContact}</Typography>}
        {moderationState === 'FIRST_OFFENSE' && details.status !== offerStatusEnum.active && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.6 }}>Review the offer’s headlines, description, terms, dates, redemption details, and image. Correct any inaccurate details and save before reactivating.</Typography>}
        {correctionRequired && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.6 }}>{correctionReason}</Typography>}
      </Alert>}
      {historicalModeration && <Box
        component="section"
        aria-label="Offer history"
        sx={{
          mx: 2,
          mt: 1.5,
          mb: 1,
          px: 2,
          py: 1.25,
          bgcolor: 'background.section',
          borderLeft: '3px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflowWrap: 'anywhere',
        }}
      >
        <Typography variant="body2" fontWeight={600}>Offer history</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>These records are historical and do not indicate an active moderation review.</Typography>
        {offenseHistory.length ? <Box component="ul" sx={{ my: 1, pl: 2.5 }}>
          {offenseHistory.map(({ offenseNumber, occurredAt, source }) => <Box component="li" key={offenseNumber} sx={{ mt: 0.75 }}>
            <Typography variant="body2" fontWeight={500}>
              {offenseNumber === 1 ? 'First offense' : offenseNumber === 2 ? 'Second offense' : `Offense ${offenseNumber}`}
              {occurredAt && moment(occurredAt).isValid() && <> · <Box component="time" dateTime={occurredAt}>{moment(occurredAt).format(dateTimeFormat)}</Box></>}
            </Typography>
            <Typography variant="body2" color="text.secondary">{source === 'TEST_SIMULATION' ? 'Test simulation; no customer feedback was recorded.' : source === 'CUSTOMER_FEEDBACK' ? 'Customers reported that this offer did not work.' : 'Moderation record retained.'}</Typography>
          </Box>)}
        </Box> : <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{feedbackExplanation || 'Offense history is retained.'}</Typography>}
      </Box>}
      <CardActions style={{ textAlign: 'right' }}>
<Box
  sx={{
    position: 'relative',
    display: 'inline-block',
    zIndex: 5, // ensure it’s above other buttons
  }}
>
  <Button
  color="primary"
  size="small"
  onClick={() => setShowPreview(true)}
  startIcon={<VisibilityIcon />}
  sx={{ textTransform: 'none' }}
>
  View offer
</Button>

  <Dialog
  open={showPreview}
  onClose={() => {
    setShowPreview(false);
    setIsLocked(false);
    clearTimeout(autoCloseTimeout);
  }}
  maxWidth="sm"
  fullWidth
>
  <OfferPreviewCard details={details} onClose={() => {
      setShowPreview(false);
      setIsLocked(false);
      clearTimeout(autoCloseTimeout);
    }} />
</Dialog>
</Box>

        {canPauseOffer(details.status) && (
          <Button disabled={readOnly} color="primary" size="small" onClick={handlePauseOffer}>
            <Pause fontSize="small" style={{ marginRight: 4 }} />
            Pause offer
          </Button>
        )}
        {canActivateOffer(details.status) && (
          <Tooltip title={correctionRequired ? correctionReason : ''}><span tabIndex={correctionRequired ? 0 : undefined}>
          <Button disabled={readOnly || correctionRequired} color="primary" size="small" onClick={handleActivateOffer}>
            <Play fontSize="small" style={{ marginRight: 4 }} />
            Activate offer
          </Button>
          </span></Tooltip>
        )}
        {!underReview && !moderationLockedArchived && (canEditOffer(details.status) || adminDisabled) && (
            <Button disabled={readOnly}
              color="primary"
              size="small"
              onClick={() => editOffer(details.offerId)}
            >
              <Edit fontSize="small" style={{ marginRight: 4 }} />
              Edit offer
            </Button>
          )}
        {(!underReview || moderationLockedArchived) &&
          <Button disabled={readOnly} color="primary" size="small" onClick={handleCloneOffer}>
            <Clone fontSize="small" style={{ marginRight: 4 }} />
            Clone offer
          </Button>
        }
        {
          <Button disabled={readOnly} color="primary" size="small" onClick={handleAddPaymentOffer}>
            <AttachMoney fontSize="small" style={{ marginRight: 4 }} />
            Add Payment
          </Button>
        }
        {canArchiveOffer(details.status) && (
          <Button disabled={readOnly}
            color="primary"
            size="small"
            onClick={() => setShowConfirmation(true)}
          >
            <Archive fontSize="small" style={{ marginRight: 4 }} />
            Archive offer
          </Button>
        )}
      </CardActions>
    </Card>
    {showConfirmation && !readOnly && (
      <ConfirmationDialog
        onCancel={() => setShowConfirmation(false)}
        onConfirm={handleArchiveOffer}
        isOpen
        body="Are you sure you want to archive this offer"
        title="Archive offer"
        successButtonLabel="Archive"
      />
    )}
  </>);
}

export default OfferCard
